<script setup lang="js">

import QuestionService from "@/services/question.service.js";
import StringUtils from '@/utils/string.utils.js';
import { ref, watch } from "vue";

const props = defineProps(['item', 'editSupportImages', 'selectedModel'])
const emits = defineEmits(['visualizeItem', 'updateItem', 'updateItems', 'removeImage'])
const generateMessage = ref('');
const isGenerating = ref(false);
const placeholderComplemento = `"Fale sobre o motor que aparece na imagem"
"Não fale sobre resistências elétricas"
"Incorpore um problema relacionado ao processo de usinagem de uma peça compleca, considerando as restrições de tempo, custo e qualidade"`;

const getAddicionalInstructionsPlaceholder = () => {
  if (props.item?.id) {
    return "Caso o item não tenha sido satisfatório, interaja com a IA sugerindo o que você gostaria de modificar ou adicionar ao para torná-la ainda mais eficaz.";
  }
  return "Caso necessário, inclua detalhes a serem considerados na geração do item.";
}

const additionalInstructionsPlaceholder = ref(getAddicionalInstructionsPlaceholder());

const generateQuestion = () => {
  QuestionService.generateQuestion(generateMessage, props.item, isGenerating, props.selectedModel, emits);
}

watch(() => props.item.id, () => {
  additionalInstructionsPlaceholder.value = getAddicionalInstructionsPlaceholder();
})

watch(() => props.item.information.supportImages, () => {
  StringUtils.checkIfLink(props.item.information.supportImages[0]?.source);
})
</script>

<template>
  <div class="grid">
    <div class="w-12 m-0 my-2">
      <h4 class="m-0">Informações selecionadas</h4>
    </div>

    <div class="w-6 m-0 mt-2">
      <label class="font-bold" for="technologicalAreaInfo">Área tecnológica: </label>
      <span id="technologicalAreaInfo">{{ props.item.information.technologicalArea?.name }}</span>
    </div>

    <div class="w-6 m-0 mt-2">
      <label class="font-bold" for="versaoVigenciaMatrizInfo">Versão de vigência da matriz: </label>
      <span id="versaoVigenciaMatrizInfo">{{ props.item.information.course?.matrixVersion }}</span>
    </div>

    <div class="w-6 m-0 mt-3">
      <label class="font-bold" for="eixoTecnologicoInfo">Eixo tecnológico: </label>
      <span id="eixoTecnologicoInfo">{{ props.item.information.technologicalArea?.technologicalAxis }}</span>
    </div>

    <div class="w-6 m-0 mt-3">
      <label class="font-bold" for="versaoInInfo">Versão IN: </label>
      <span id="versaoInInfo">{{ props.item.information.course?.inVersion }}</span>
    </div>

    <div class="w-6 m-0 mt-3">
      <label class="font-bold" for="cursoInfo">Curso: </label>
      <span id="cursoInfo">{{ props.item.information.course?.name }}</span>
    </div>

    <div class="w-12 m-0 mt-3">
      <label class="font-bold" for="competenciaGeralInfo">Competência geral: </label>
      <span id="competenciaGeralInfo">{{ props.item.information.course?.generalCompetence }}</span>
    </div>

    <div class="w-12 m-0 mt-3">
      <label class="font-bold" for="capacidadeInfo">Capacidade: </label>
      <span id="capacidadeInfo">{{ props.item.information.capacity?.name }}</span>
    </div>

    <div class="w-12 m-0 mt-3">
      <label class="font-bold" for="taxonomyInfo">Nível cognitivo avaliado (Taxonomia de Bloom): </label>
      <span id="capacidadeInfo">{{ props.item.information.capacity?.taxonomy }}</span>
    </div>

    <div class="w-12 m-0 mt-3">
      <label class="font-bold" for="funcaoInfo">Função: </label>
      <span id="funcaoInfo">{{ props.item.information.subfunction?.function }}</span>
    </div>

    <div class="w-12 m-0 mt-3">
      <label class="font-bold" for="subfunctionInfo">Subfunção: </label>
      <span id="subfunctionInfo">{{ props.item.information.subfunction?.name }}</span>
    </div>

    <div class="w-12 m-0 mt-3">
      <label class="font-bold" for="conhecimentosInfo">Conhecimentos: </label>
      <ul id="conhecimentosInfo">
        <li v-for="knowledge in props.item.information.knowledge" :key="knowledge.idKnowledge">{{ knowledge.name }}</li>
      </ul>
    </div>

    <div class="w-full flex justify-content-between">
      <div class="w-4">
        <label class="font-bold" for="difficultyLevelInfo">Nível de dificuldade: </label>
        <span id="difficultyLevelInfo">{{ props.item.information.difficultyLevel?.label }}</span>
      </div>

      <div class="w-4">
        <label class="font-bold" for="formatInfo">Formato do item: </label>
        <span id="formatInfo">{{ props.item.information.format?.label }}</span>
      </div>

      <div class="w-4">
        <label class="font-bold" for="numberOfAlternativesInfo">Quantidade de alternativas: </label>
        <span id="numberOfAlternativesInfo">{{ props.item.information.numberOfAlternatives }}</span>
      </div>
    </div>

    <Divider class="mb-0"/>

    <Card class="card-image" v-if="props.item.information.supportImages?.length">
      <template #title>Imagem de suporte</template>
      <template #content>
        <img
            :src="props.item.information.supportImages[0]?.base64 || ''"
            :alt="props.item.information.supportImages[0].description"
            class="image-preview"
        />
        <div class="mt-2">
          <span class="mr-1 font-bold">Descrição:</span>
          <span class="">{{ props.item.information.supportImages[0].description ?? 'Não informado' }}</span>
        </div>
        <Divider/>

        <div class="flex">
          <span class="text-source-image m-0 mr-1 font-bold">Fonte:</span>
          <a
              v-if="StringUtils.checkIfLink(props.item.information.supportImages[0].source)"
              :href="props.item.information.supportImages[0].source"
              target="_blank"
              class="text-source-image"
          >{{ props.item.information.supportImages[0].source }}
          </a>
          <span v-else class="text-source-image">{{ props.item.information.supportImages[0].source ?? 'Não informado' }}</span>
        </div>
      </template>
      <template #footer>
        <div class="flex gap-4 mt-1">
          <Button
              label="Editar"
              class="w-full"
              icon="pi pi-pencil"
              @click="props.editSupportImages"
          />
          <Button
              label="Remover"
              severity="secondary"
              outlined
              class="w-full"
              icon="pi pi-trash"
              @click="$emit('removeImage')"
          />
        </div>
      </template>
    </Card>

    <Divider class="mb-0" v-if="props.item.information.supportImages?.length"/>

    <div class="w-12 m-0 mt-2">
      <Fieldset legend="Instruções complementares para a IA generativa">
        <span>{{ additionalInstructionsPlaceholder }}</span>

        <Textarea
            id="additionalInstructions"
            rows="5"
            v-model="props.item.information.additionalInstructions"
            :placeholder='placeholderComplemento'
            class="w-12 mt-1"
        />
      </Fieldset>
    </div>

    <div class="w-12 m-2 flex justify-content-end">
      <span v-if="generateMessage"
            id="generateMessage"
            :class="'mr-3' + (generateMessage.error ? ' text-red-error' : '')"
      >{{ generateMessage.message }}</span>

      <Button
          id="generateQuestion"
          label="Gerar"
          icon="pi pi-check"
          @click="generateQuestion()"
      />
    </div>
  </div>
</template>

<style scoped>
.card-image {
  width: 35rem;
  overflow: hidden;
}

.image-preview {
  width: 100%;
  max-width: 35rem;
  border-radius: 8px;
}
</style>